import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { BannerOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { ratioLabel } from './bannerPreview';
import type { PreviewModel } from './bannerPreview';

interface BannerBasicInfoCardProps {
  record: BannerOut;
  /** Layout and ratio are read from the preview, never re-derived. */
  model: PreviewModel;
}

export const BannerBasicInfoCard = ({
  record,
  model,
}: BannerBasicInfoCardProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();

  const key = (suffix: string) => `user_catalog.banners.${suffix}`;
  const count = model.entries.length;

  const layout =
    count === 0
      ? t(key('layout.empty'))
      : model.isCarousel
        ? t(key('layout.carousel'), { count })
        : t(key('layout.static'));

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: orDash(
        record.id != null && <Typography.Text code>{record.id}</Typography.Text>
      ),
    },
    {
      key: 'name',
      label: t(key('fields.name')),
      children: orDash(record.name),
    },
    {
      key: 'layout',
      label: t(key('fields.layout')),
      children: layout,
    },
    {
      key: 'ratio',
      label: t(key('fields.ratio')),
      children: orDash(
        model.effectiveSize &&
          t(key('fields.ratio_value'), {
            ratio: ratioLabel(model.effectiveSize),
            source: model.ratioSourcePosition,
          })
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
    ...(record.description
      ? [
          {
            key: 'description',
            label: t(key('fields.description')),
            span: 3,
            children: (
              <Typography.Paragraph
                style={{ margin: 0, whiteSpace: 'pre-wrap' }}
              >
                {record.description}
              </Typography.Paragraph>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card title={t('base.sections.basic_info')}>
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2, md: 3 }}
        items={items}
      />
    </Card>
  );
};
